import { FirebaseApp, FirebaseOptions, getApp, getApps, initializeApp } from 'firebase/app';
import { Auth, User, getAuth, onAuthStateChanged } from 'firebase/auth';
import { Firestore, getFirestore } from 'firebase/firestore';
import { Functions, getFunctions } from 'firebase/functions';

const firebaseConfig: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const requiredKeys: Record<string, string | undefined> = {
  VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
};

const missing = Object.entries(requiredKeys)
  .filter(([, value]) => !value)
  .map(([key]) => key);

if (missing.length) {
  console.warn(
    `Missing Firebase environment variables for web app: ${missing.join(', ')}. Update your Vite env file.`,
  );
}

let firebaseApp: FirebaseApp | undefined;
let firebaseAuth: Auth | undefined;

export const getFirebaseApp = (): FirebaseApp => {
  if (firebaseApp) {
    return firebaseApp;
  }
  firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);
  return firebaseApp;
};

export const getFirebaseAuth = (): Auth => {
  if (firebaseAuth) {
    return firebaseAuth;
  }
  firebaseAuth = getAuth(getFirebaseApp());
  return firebaseAuth;
};

export const app: FirebaseApp = getFirebaseApp();
export const auth: Auth = getFirebaseAuth();
export const db: Firestore = getFirestore(app);
export const functions: Functions = getFunctions(app, 'us-central1');

const callableBaseUrl = `https://us-central1-${firebaseConfig.projectId}.cloudfunctions.net`;

type CallableSuccess<T> = { result: T };
type CallableError = {
  error?: {
    status?: string;
    message?: string;
    details?: unknown;
  };
};
type CallableResponse<T> = CallableSuccess<T> | CallableError;

const isCallableError = (payload: CallableResponse<unknown> | null): payload is CallableError =>
  Boolean(payload && 'error' in payload);

const getCurrentUser = async (authInstance: Auth): Promise<User | null> => {
  if (authInstance.currentUser) {
    return authInstance.currentUser;
  }

  return new Promise(resolve => {
    const unsubscribe = onAuthStateChanged(authInstance, user => {
      unsubscribe();
      resolve(user);
    });
  });
};

const getIdTokenIfAvailable = async (authInstance: Auth): Promise<string | undefined> => {
  try {
    const user = await getCurrentUser(authInstance);
    if (!user) {
      return undefined;
    }
    return user.getIdToken();
  } catch (error) {
    console.warn('Failed to fetch ID token for callable functions', error);
    return undefined;
  }
};

export async function callCallableFunction<TRequest extends Record<string, unknown>, TResponse>(
  name: string,
  data: TRequest,
): Promise<TResponse> {
  const authInstance = getFirebaseAuth();
  const idToken = await getIdTokenIfAvailable(authInstance);
  const url = `${callableBaseUrl}/${name}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ data }),
  });

  let payload: CallableResponse<TResponse> | null = null;

  try {
    payload = (await response.json()) as CallableResponse<TResponse>;
  } catch (error) {
    console.warn('Failed to parse callable response payload', error);
  }

  if (!response.ok || !payload || isCallableError(payload)) {
    const errorMessage = payload && isCallableError(payload) ? payload.error?.message : undefined;
    const message = errorMessage || response.statusText || 'Callable function error';
    throw new Error(message);
  }

  return payload.result;
}
